import { useCallback, useState } from 'react';

import { useMutation } from '@apollo/client';
import { updateChannel } from 'lane-shared/graphql/mutation';
import { castForUpdate } from 'lane-shared/helpers';

import { BulkModuleDefinition } from '../registry/bulkModuleRegistry';

const CONCURRENCY = 5;

type ApplyResult = { succeededIds: string[]; failedIds: string[] };
type ApplyState = { applying: boolean; result: ApplyResult | null; error: Error | null };

const INITIAL_STATE: ApplyState = { applying: false, result: null, error: null };

/**
 * Applies a module's enable patch to a list of channels in parallel batches.
 *
 * Uses the same `updateChannel` + `castForUpdate` path as `ChannelProfile.onSave`
 * so behaviour is identical to the per-channel save. Concurrency is capped at
 * CONCURRENCY to avoid flooding the server.
 *
 * Partial failures are captured so the caller can surface "X succeeded, Y failed"
 * rather than aborting the entire run.
 */
export function useApplyBulkModuleEnable() {
  const [state, setState] = useState<ApplyState>(INITIAL_STATE);
  const [mutate] = useMutation(updateChannel);

  const apply = useCallback(
    async (channelIds: string[], moduleDefinition: BulkModuleDefinition): Promise<ApplyResult> => {
      setState({ applying: true, result: null, error: null });

      const succeededIds: string[] = [];
      const failedIds: string[] = [];

      try {
        for (let i = 0; i < channelIds.length; i += CONCURRENCY) {
          const batch = channelIds.slice(i, i + CONCURRENCY);

          const batchResults = await Promise.allSettled(
            batch.map(channelId =>
              mutate({
                variables: {
                  channel: castForUpdate({
                    _id: channelId,
                    ...moduleDefinition.buildEnablePatch(),
                  }),
                },
              })
            )
          );

          batchResults.forEach((outcome, index) => {
            if (outcome.status === 'fulfilled') {
              succeededIds.push(batch[index]);
            } else {
              failedIds.push(batch[index]);
            }
          });
        }

        const result: ApplyResult = { succeededIds, failedIds };
        setState({ applying: false, result, error: null });
        return result;
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error));
        setState({ applying: false, result: null, error: err });
        throw err;
      }
    },
    [mutate]
  );

  const reset = useCallback(() => setState(INITIAL_STATE), []);

  return { ...state, apply, reset };
}
